import { useState, useCallback, useRef } from "react";

export interface UseInfiniteScrollOptions<T> {
  /** Initial items already loaded (e.g. first page from parent). */
  initialItems: T[];
  /** Total number of items available server-side. */
  totalCount: number;
  /** Number of items per page. */
  pageSize: number;
  /** Async function that fetches the next page given (offset, limit).  */
  fetchPage: (offset: number, limit: number) => Promise<T[]>;
}

export interface UseInfiniteScrollResult<T> {
  /** All items loaded so far. */
  items: T[];
  /** Whether more items are available to load. */
  hasMore: boolean;
  /** Whether a page is currently being fetched. */
  isLoading: boolean;
  /** Trigger loading the next page. Safe to call multiple times; concurrent fetches are de-duped. */
  loadMore: () => void;
  /** Reset state to a new set of initial items / totalCount (e.g. when patient changes). */
  reset: (initialItems: T[], totalCount: number) => void;
}

/**
 * Generic hook for paginated / infinite-scroll data.
 *
 * It keeps an internal list of items, tracks the current offset,
 * and exposes a `loadMore` callback that appends the next page.
 */
export function useInfiniteScroll<T>({
  initialItems,
  totalCount,
  pageSize,
  fetchPage,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollResult<T> {
  const [items, setItems] = useState<T[]>(initialItems);
  const [total, setTotal] = useState(totalCount);
  const [isLoading, setIsLoading] = useState(false);

  // Use a ref for offset so loadMore always reads the latest value
  const offsetRef = useRef(initialItems.length);
  const loadingRef = useRef(false);

  const hasMore = items.length < total;

  const loadMore = useCallback(() => {
    if (loadingRef.current || offsetRef.current >= total) return;
    loadingRef.current = true;
    setIsLoading(true);

    const currentOffset = offsetRef.current;
    fetchPage(currentOffset, pageSize)
      .then((newItems) => {
        setItems((prev) => [...prev, ...newItems]);
        offsetRef.current = currentOffset + newItems.length;
      })
      .catch((err) => {
        console.error("[useInfiniteScroll] Failed to fetch page:", err);
      })
      .finally(() => {
        loadingRef.current = false;
        setIsLoading(false);
      });
  }, [fetchPage, pageSize, total]);

  const reset = useCallback((newItems: T[], newTotal: number) => {
    setItems(newItems);
    setTotal(newTotal);
    offsetRef.current = newItems.length;
    loadingRef.current = false;
    setIsLoading(false);
  }, []);

  return { items, hasMore, isLoading, loadMore, reset };
}
